import { uniqueId } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Row, Spinner } from "react-bootstrap";
import {
  ArrowUpRightCircle,
  ExclamationCircleFill,
  ExclamationTriangleFill,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { makeApiRequests } from "../../helpers/api";
import { MAX_CHUNK_SIZE } from "../../helpers/constants";
import Tooltip from "react-simple-tooltip";

const DriveFileUploader = ({
  onUploadedFilesChange,
  filesToBeUploaded = [],
  multiple = true,
  id,
  allowedFileTypes = [], // Example ['image/gif', 'image/jpeg', 'image/png']
  accept = "", // Only shows this type of file in file selection window
  size = "sm",
  label = "Upload",
  required = true,
  onFileSave,
  deletable = true,
  title,
  preFilledFiles = [],
  hideUploader,
  folderId,
  showFolder = false,
  showError,
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInput = useRef(null);

  const sendChunk = async (
    chunk,
    chunkIndex,
    fileName,
    mimeType,
    fileSize,
    byteStart,
    byteEnd,
    totalChunks
  ) => {
    const fileInfoObject = {
      fileChunk: chunk,
      chunkIndex,
      fileName,
      totalChunks,
      mimeType,
      fileSize,
      byteStart,
      byteEnd,
      folderId,
    };

    const { error, response } = await makeApiRequests({
      requestType: "upload-file",
      requestBody: fileInfoObject,
    });

    if (error) {
      return toast.error(error);
    }

    return response;
  };

  const onFileChange = async (e) => {
    let files = e.target.files ? [...e.target.files] : [];

    if (!files.length) return;

    if (files.some((f) => f.size > MAX_CHUNK_SIZE)) {
      return toast.error(
        "Some of the files you uploaded exceeds the allowed 48MB limit and are ignored"
      );
    }

    files = files.filter((f) => f.size <= MAX_CHUNK_SIZE);

    filesToBeUploaded.push(
      ...Array.from(files).map((file) => ({
        file,
        status: "pending",
        id: uniqueId(),
      }))
    );
    onUploadedFilesChange([...filesToBeUploaded]);

    if (allowedFileTypes?.length) {
      const allowedFiles = Array.from(filesToBeUploaded)?.filter((file) =>
        allowedFileTypes.includes(file["type"])
      );

      if (filesToBeUploaded?.length !== allowedFiles?.length) {
        if (multiple) {
          let fileDifference = filesToBeUploaded?.length - allowedFiles?.length;
          toast.error(
            `${fileDifference}/${filesToBeUploaded?.length} ${
              fileDifference < 2 ? "File Is" : "Files Are"
            } Not Suitable To Upload.`
          );
        } else {
          toast.error("This File Is Not Suitable To Upload.");
        }
        filesToBeUploaded = allowedFiles;
      }
    }

    await Promise.all(
      filesToBeUploaded
        ?.filter((uploadedFile) => uploadedFile.status === "pending")
        .map(async ({ file, id }) => {
          setUploadProgress(0);
          const totalChunks = Math.ceil(file.size / MAX_CHUNK_SIZE);

          let chunkIndex = 0;

          for (chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * MAX_CHUNK_SIZE;
            const end = Math.min(start + MAX_CHUNK_SIZE, file.size);
            const chunk = file?.slice(start, end);

            const base64data = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (event) => {
                resolve(event.target.result.split(",")[1]);
              };
              reader.onerror = (error) => {
                reject(error);
              };
              reader.readAsDataURL(chunk);
            });
            const currentlyUploadingFile = filesToBeUploaded.findIndex(
              (uploadedFile) => uploadedFile?.id === id
            );

            try {
              const response = await sendChunk(
                base64data,
                chunkIndex,
                file.name,
                file.type,
                file.size,
                start,
                end - 1,
                totalChunks,
                folderId
              );

              if (response.status === "pending") {
                setUploadProgress(((chunkIndex + 1) / totalChunks) * 100);
              } else if (response.status === "complete") {
                setUploadProgress(100);
                if (!onFileSave) {
                  filesToBeUploaded[currentlyUploadingFile] = {
                    ...filesToBeUploaded[currentlyUploadingFile],
                    status: "complete",
                    fileId: response?.fileId,
                  };
                } else {
                  onFileSave({ fileId: response?.fileId })
                    .then(() => {
                      filesToBeUploaded[currentlyUploadingFile] = {
                        ...filesToBeUploaded[currentlyUploadingFile],
                        status: "complete",
                        fileId: response?.fileId,
                      };

                      onUploadedFilesChange([...filesToBeUploaded]);
                    })
                    .catch(() => {
                      filesToBeUploaded[currentlyUploadingFile] = {
                        ...filesToBeUploaded[currentlyUploadingFile],
                        status: "error",
                      };
                    });
                  onUploadedFilesChange([...filesToBeUploaded]);
                }
              } else {
                toast.error(response?.e);
                filesToBeUploaded[currentlyUploadingFile] = {
                  ...filesToBeUploaded[currentlyUploadingFile],
                  status: "error",
                };
                onUploadedFilesChange([...filesToBeUploaded]);
                break;
              }
              onUploadedFilesChange([...filesToBeUploaded]);
            } catch (error) {
              console.error("Error during upload:", error);
              filesToBeUploaded[currentlyUploadingFile] = {
                ...filesToBeUploaded[currentlyUploadingFile],
                status: "error",
              };
              onUploadedFilesChange([...filesToBeUploaded]);
              break;
            }
          }
        })
    );
  };

  const count = useMemo(() => filesToBeUploaded?.length, [filesToBeUploaded]);

  const removeFile = (fileId) => {
    onUploadedFilesChange([
      ...filesToBeUploaded.filter(
        (uploadedFile) => uploadedFile?.id !== fileId
      ),
    ]);
  };

  return (
    <>
      <div className="d-flex justify-content-start align-items-center flex-wrap">
        {title && (
          <>
            <h6 className="small-text">{title}</h6>
            {showFolder && folderId && (
              <a
                style={{
                  textDecoration: "underline",
                }}
                className="ml-2 small-text mb-2"
                target="_blank"
                href={`https://drive.google.com/drive/folders/${folderId}`}
              >
                open folder <ArrowUpRightCircle className="align-text-bottom" />
              </a>
            )}
          </>
        )}
        {showError && (
          <Tooltip background={"#C50000"} content={showError} fontSize={"12px"}>
            <ExclamationTriangleFill
              title={showError}
              className="ml-2 text-danger mb-2"
              id="error"
            />
          </Tooltip>
        )}
      </div>

      {preFilledFiles?.length === 0 && (
        <h6 className="tinner-text">No Files Found</h6>
      )}
      {preFilledFiles && preFilledFiles.length > 0 && (
        <Row className="my-1">
          {preFilledFiles.map((value) => (
            <Col xs={12} md={3}>
              <Link
                style={{ textDecoration: "none" }}
                target="_blank"
                to={`https://drive.google.com/file/d/${value?.id}/view`}
              >
                <Alert
                  className={`text-left mb-1 p-1 ${
                    size === "sm" ? "tiny-text" : "small-text"
                  } `}
                  variant="info"
                >
                  {value?.fileName}
                </Alert>
              </Link>
            </Col>
          ))}
        </Row>
      )}
      {!hideUploader && !showError && (
        <div
          className={`border border-muted rounded p-${
            size === "sm" ? "1" : "2"
          } text-center`}
        >
          <div>
            {count > 0 ? (
              <>
                {filesToBeUploaded.length && (
                  <div className="mb-1">
                    {filesToBeUploaded.map(({ file, id, status }, index) => {
                      return (
                        <Button
                          key={index}
                          name={`${id}-file`}
                          data-name={file.name}
                          data-mimetype={file.mimetype}
                          data-value={file.data}
                          className="p-1 mr-1 mb-1"
                          variant="primary"
                          onClick={(e) =>
                            status === "complete" && deletable
                              ? removeFile(id)
                              : e.stopPropagation()
                          }
                          size={size}
                        >
                          <h6 className="tiny-text p-0 m-0">
                            {" "}
                            {file?.name}{" "}
                            <span className="ml-2">
                              {" "}
                              {status === "pending" && (
                                <Spinner
                                  size="sm"
                                  variant="light"
                                  animation="border"
                                  className="image-loading-spinner"
                                />
                              )}
                              {status === "complete" && deletable && "x"}
                              {status === "error" && (
                                <ExclamationCircleFill className="text-danger" />
                              )}
                            </span>
                          </h6>
                        </Button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <h6
                className={`mb-0 p-0 text-center text-secondary ${
                  size === "sm" ? "tiny-text" : ""
                }`}
                variant="primary"
              >
                No files uploaded
              </h6>
            )}
          </div>
          {!filesToBeUploaded.some(
            (fileToBeUploaded) => fileToBeUploaded?.status === "pending"
          ) &&
            !filesToBeUploaded.some((fileToBeUploaded) =>
              !multiple ? fileToBeUploaded?.status === "complete" : false
            ) && (
              <>
                <hr className="m-0" />

                <Button
                  className="mt-2 p-1"
                  variant="outline-info"
                  onClick={() => fileInput.current.click()}
                  size={size}
                >
                  <h6 className="tiny-text p-0 m-0">{label}</h6>
                </Button>

                <input
                  ref={fileInput}
                  type="file"
                  onChange={onFileChange}
                  id={id}
                  style={{ display: "none" }}
                  multiple={multiple}
                  accept={accept}
                  required={required}
                />
              </>
            )}
        </div>
      )}
    </>
  );
};

export default DriveFileUploader;
